import {
    useCallback, useMemo, useState,
} from 'react'
import noop from 'lodash/noop'
import useSimpleApiRequest from 'app/hooks/useSimpleApiRequest'
import ButtonContainer from 'app/shared-components/ButtonContainer'
import StatusHandler from 'app/shared-components/StatusHandler'

import {
    LaneFeesStatus,
} from 'app/enums/label.enums'
import {
    useChangeValidity,
} from 'app/shared-components/ChangeValidity'
import useRequestWithConfirmation from 'app/hooks/useRequestWithConfirmation'
import {
    ArchiveButton, ArchiveConfirmationDialog, useArchive,
} from 'app/shared-components/Archive'
import Button from 'app/shared-components/Button'
import {
    useCollapsibleTableContext,
} from 'app/shared-components/CollapsibleTable'
import useGetLaneFee from 'app/Apps/Pricing/LaneGroups/LaneFees/hooks/useGetLaneFee'
import LaneFeesRequests from 'app/requests/ContractBasis/LaneFees.requests'
import useLaneGroupContext from 'app/Apps/Pricing/LaneGroups/hooks/useLaneGroupContext'
import {
    generateSkyNetLink,
} from 'app/SkyNetRoutes'
import usePricingRoutes from 'app/SkyNetRoutes/hooks/usePricingRoutes'
import RejectLaneFeesConfirmationDialog
    from 'app/Apps/AccountManagement/components/AwardRejectLaneFees/RejectLaneFeesConfirmationDialog'

import LaneFeesForm from '../LaneFeesForm'
import LaneFeeChangeValidity from './LaneFeeChangeValidity/LaneFeeChangeValidity'
import LaneFeeCancellation from './LaneFeeCancellation'
import ReviewLaneFeesReturnReason from './ReviewLaneFeesReturnReason'
import useChangeLaneFeesStatus from './hooks/useChangeLaneFeesStatus'
import ReviewLaneFeesRejectReason from './ReviewLaneFeesRejectReason'
import ReturnLaneFeesConfirmationDialog from './ReturnLaneFeesConfirmationDialog'

export enum LaneFeesActionButtons {
    Copy = 'copy',
    Archive = 'archive',
    SetInProgress = 'set-in-progress',
    SetToRejected = 'set-to-rejected',
    SetToAwarded = 'set-to-awarded',
    SetAsSubmitted = 'set-as-submitted',
    SetToCancelled = 'set-to-cancelled',
    ChangeValidity = 'change-validity',
}

export const defaultButtonsConfig: Record<LaneFeesStatus, LaneFeesActionButtons[]> = {
    [LaneFeesStatus.IN_PROGRESS]: [
        LaneFeesActionButtons.Copy,
        LaneFeesActionButtons.SetAsSubmitted,
        LaneFeesActionButtons.SetToCancelled,
    ],
    [LaneFeesStatus.SUBMITTED]: [
        LaneFeesActionButtons.Copy,
        LaneFeesActionButtons.SetInProgress,
        LaneFeesActionButtons.SetToAwarded,
        LaneFeesActionButtons.SetToRejected,
        LaneFeesActionButtons.SetToCancelled,
        LaneFeesActionButtons.ChangeValidity,
    ],
    [LaneFeesStatus.REJECTED]: [
        LaneFeesActionButtons.Copy,
        LaneFeesActionButtons.SetToAwarded,
        LaneFeesActionButtons.SetToCancelled,
    ],
    [LaneFeesStatus.AWARDED]: [
        LaneFeesActionButtons.Copy,
        LaneFeesActionButtons.SetToCancelled,
        LaneFeesActionButtons.ChangeValidity,
    ],
    [LaneFeesStatus.CANCELLED]: [
        LaneFeesActionButtons.Copy,
        LaneFeesActionButtons.Archive,
    ],
}

export default function LaneFeesUpdate({
    id,
    refetch = noop,
    buttonsConfig = defaultButtonsConfig,
    customHandlers,
    disabled = false,
}: Readonly<{
    id?: number,
    refetch?: (...args: any[]) => void,
    buttonsConfig?: Record<LaneFeesStatus, LaneFeesActionButtons[]>,
    disabled?: boolean,
    customHandlers?: Partial<Record<LaneFeesActionButtons, JSX.Element>>
}>) {
    const {
        laneGroupId,
    } = useLaneGroupContext()

    const {
        LaneGroups: LaneGroupsRoutes,
    } = usePricingRoutes()

    const {
        updatePathWithParams,
        updatePathWithId,
    } = useCollapsibleTableContext() || {}

    const {
        data,
        isDataReady,
        isFetching,
        isError,
        error,
        invalidate,
    } = useGetLaneFee(id)

    const [
        cancelationReason,
        setCancelationReason,
    ] = useState('')
    const [
        openReviewLaneFeesReturnConfirm,
        setOpenReviewLaneFeesReturnConfirm,
    ] = useState(false)

    const requestParam = useMemo(() => {
        if (data?.laneFeesStatus !== LaneFeesStatus.IN_PROGRESS) {
            return LaneFeesRequests.editableData({
                laneFeesId: data?.id,
            })
        }
        return LaneFeesRequests.update({
            laneFeesId: data?.id,
        })
    }, [data])

    const onSuccess = useCallback(() => {
        invalidate()
        refetch()
    }, [
        invalidate,
        refetch,
    ])

    const archive = useArchive({
        requestParamFn: data?.archived ? LaneFeesRequests.restore
            : LaneFeesRequests.archive,
        notificationId: 'LaneFeeUpdateDto',
        title: 'Lane Fee',
        data,
        redirectionAfterSuccess: generateSkyNetLink({
            domainPath: LaneGroupsRoutes.LaneFees.path,
            params: {
                key: laneGroupId,
            },
        }),
        onSuccess: refetch,
    })

    const {
        changeStatus,
    } = useChangeLaneFeesStatus({
        notificationId: 'LaneFeeUpdateDto',
        onSuccess,
    })

    const cancelFee = useRequestWithConfirmation({
        requestParamFn: LaneFeesRequests.cancellation,
        notificationId: 'LaneFeesUpdateDto',
        title: 'Lane Fee',
        data: {
            ...data,
            commentForBilling: cancelationReason,
        },
        actionDone: 'Cancelled',
        onSuccess,
    })

    const onChangeValiditySuccess = useCallback(() => {
        onSuccess()
        LaneGroupsRoutes.LaneFees.navigate({
            params: {
                key: laneGroupId,
                feesId: data?.id,
            },
        })
    }, [
        LaneGroupsRoutes,
        onSuccess,
        data?.id,
        laneGroupId,
    ])

    const changeValidity = useChangeValidity({
        requestParamFn: LaneFeesRequests.validity,
        notificationId: 'LaneFeesUpdateDto',
        title: 'Lane Fee',
        data: {
            id: data?.id,
            validFrom: data?.validFrom,
            validTo: data?.validTo,
        },
        onSuccess: onChangeValiditySuccess,
    })

    const handleToSubmitted = useCallback(() => {
        changeStatus({
            id,
            status: LaneFeesStatus.SUBMITTED,
            title: 'Lane Fee Status changes to Submitted',
        })
    }, [
        changeStatus,
        id,
    ])

    const handleToAwarded = useCallback(() => {
        changeStatus({
            id,
            status: LaneFeesStatus.AWARDED,
            title: 'Lane Fee Status changes to Awarded',
        })
    }, [
        changeStatus,
        id,
    ])

    const [
        toRejectedConfirmation,
        setToRejectedConfirmation,
    ] = useState(false)

    const [
        returnToInProgressConfirmation,
        setReturnToInProgressConfirmation,
    ] = useState(false)

    const actionButtons: Record<LaneFeesActionButtons, JSX.Element> = useMemo(() => {
        return {
            [LaneFeesActionButtons.Copy]: <Button
                label="Copy"
                key={LaneFeesActionButtons.Copy}
                name={LaneFeesActionButtons.Copy}
                onClick={() => {
                    updatePathWithParams?.({
                        action: 'copy',
                        id: data?.id,
                    })
                }}
            />,
            [LaneFeesActionButtons.Archive]: <ArchiveButton
                key={LaneFeesActionButtons.Archive}
                onClick={archive.handleArchiveRestore}
                archived={data?.archived}
            />,
            [LaneFeesActionButtons.SetInProgress]: <Button
                key={LaneFeesActionButtons.SetInProgress}
                name={LaneFeesActionButtons.SetInProgress}
                label="Set to in Progress"
                onClick={() => {
                    setReturnToInProgressConfirmation(true)
                }}
            />,
            [LaneFeesActionButtons.SetToRejected]: <Button
                key={LaneFeesActionButtons.SetToRejected}
                name={LaneFeesActionButtons.SetToRejected}
                label="Set to Rejected"
                onClick={() => {
                    setToRejectedConfirmation(true)
                }}
            />,
            [LaneFeesActionButtons.SetToAwarded]: <Button
                key={LaneFeesActionButtons.SetToAwarded}
                name={LaneFeesActionButtons.SetToAwarded}
                label="Set to Awarded"
                onClick={handleToAwarded}
            />,
            [LaneFeesActionButtons.SetAsSubmitted]: <Button
                key={LaneFeesActionButtons.SetAsSubmitted}
                name={LaneFeesActionButtons.SetAsSubmitted}
                label="Set to Submitted"
                onClick={handleToSubmitted}
            />,
            [LaneFeesActionButtons.SetToCancelled]: <Button
                key={LaneFeesActionButtons.SetToCancelled}
                name={LaneFeesActionButtons.SetToCancelled}
                label="Set to Cancelled"
                onClick={cancelFee.askConfirmation}
            />,
            [LaneFeesActionButtons.ChangeValidity]: <Button
                key={LaneFeesActionButtons.ChangeValidity}
                name={LaneFeesActionButtons.ChangeValidity}
                label="Change Validity"
                onClick={changeValidity.handleChangeValidity}
            />,
            ...customHandlers,
        }
    }, [
        archive.handleArchiveRestore,
        data?.archived,
        data?.id,
        handleToAwarded,
        handleToSubmitted,
        cancelFee.askConfirmation,
        changeValidity.handleChangeValidity,
        customHandlers,
        updatePathWithParams,
    ])

    const onCancel = useCallback(() => {
        invalidate()
        updatePathWithId?.({
            id: undefined,
        })
    }, [
        invalidate,
        updatePathWithId,
    ])

    const toRejectHandler = useCallback((rejectReasonData) => {
        changeStatus({
            id,
            status: LaneFeesStatus.REJECTED,
            data: rejectReasonData,
            title: 'Lane Fee Status changes to Rejected',
        })
    }, [
        changeStatus,
        id,
    ])

    const returnToInProgressHandler = useCallback((reasonData) => {
        changeStatus({
            id,
            status: LaneFeesStatus.IN_PROGRESS,
            data: reasonData,
            title: 'Lane Fee Status changes to In Progress',
        })
    }, [
        changeStatus,
        id,
    ])

    const [
        openReviewLaneFeesRejecteDialog,
        setOpenReviewLaneFeesRejectedDialog,
    ] = useState(false)

    const {
        mutate: update,
        isPending,
    } = useSimpleApiRequest({
        onSuccess,
        name: 'LaneFeesUpdate',
        requestParams: requestParam,
        title: 'Lane Fees Updated',
    })

    const getActionButtonsPanel = useCallback(({
        value, isValid, edited,
    }) => {
        return (
            <ButtonContainer>
                <Button
                    disabled={!isValid || !edited}
                    label="Save"
                    onClick={() => {
                        update(value)
                    }}
                    saving={isPending}
                />
                {
                    (buttonsConfig?.[data?.laneFeesStatus] || []).map((button) => {
                        return actionButtons[button]
                    })
                }
                <Button
                    disabled={!edited || isPending}
                    secondary
                    label="Cancel"
                    onClick={onCancel}
                />
            </ButtonContainer>
        )
    }, [
        actionButtons,
        buttonsConfig,
        data?.laneFeesStatus,
        isPending,
        onCancel,
        update,
    ])

    return (
        <StatusHandler
            isSuccess={isDataReady}
            isFetching={isFetching}
            isError={isError}
            error={error}
        >
            <>
                <LaneFeesForm
                    data={data}
                    disabled={disabled}
                    getActionButtonsPanel={getActionButtonsPanel}
                    setOpenReviewLaneFeesReturnConfirm={setOpenReviewLaneFeesReturnConfirm}
                    setOpenReviewLaneFeesRejectedDialog={setOpenReviewLaneFeesRejectedDialog}
                />
                <LaneFeeChangeValidity
                    id={data?.id}
                    changeValidity={changeValidity}
                />
                <LaneFeeCancellation
                    id={data?.id}
                    cancelFee={cancelFee}
                    setReason={setCancelationReason}
                    reason={cancelationReason}
                />
                <ArchiveConfirmationDialog
                    title="Lane Fees"
                    openConfirmDialog={archive.openConfirmDialog}
                    handleClose={archive.handleArchiveRestoreConfirm}
                    archived={data?.archived}
                />
                <ReviewLaneFeesReturnReason
                    openConfirmDialog={openReviewLaneFeesReturnConfirm}
                    handleClose={() => {
                        return setOpenReviewLaneFeesReturnConfirm(false)
                    }}
                    data={{
                        returnCreatedBy: data?.returnCreatedBy,
                        returnCreatedOn: data?.returnCreatedOn,
                        returnReason: data?.returnReason,
                        returnReasonComment: data?.returnReasonComment,
                    }}
                />
                <RejectLaneFeesConfirmationDialog
                    open={toRejectedConfirmation}
                    action={toRejectHandler}
                    close={() => {
                        setToRejectedConfirmation(false)
                    }}
                />
                <ReviewLaneFeesRejectReason
                    openConfirmDialog={openReviewLaneFeesRejecteDialog}
                    handleClose={() => {
                        return setOpenReviewLaneFeesRejectedDialog(false)
                    }}
                    data={{
                        rejectReason: data?.rejectedReason,
                        rejectReasonComment: data?.rejectedReasonComment,
                        rejectCreatedBy: data?.rejectedBy,
                        rejectCreatedOn: data?.rejectedOn,
                    }}
                />
                <ReturnLaneFeesConfirmationDialog
                    open={returnToInProgressConfirmation}
                    close={() => {
                        setReturnToInProgressConfirmation(false)
                    }}
                    action={returnToInProgressHandler}
                />
            </>
        </StatusHandler>
    )
}
